import { Button } from 'admin/components/atoms/Buttons';
import DateTimeRange from 'admin/components/atoms/DateTimeRange';
import Headings from 'admin/components/atoms/Headings';
import Labeled from 'admin/components/atoms/Labeled';
import TextBox from 'admin/components/atoms/TextBox';
import LabeledTextBox from 'admin/components/molecules/LabeledTextBox';
import MoneySelector from 'admin/components/organisms/MoneySelector';
import moment, { Moment } from 'moment';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import {
  NumberParam,
  QueryParamConfig,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { useAppSelector } from '../../../hooks/useApp';
import { selectUserPrivateMoney } from '../../../reducers/user-private-money';
import { getQueryParamDefaultValues } from '../../../utils/use-query-params';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';

type CouponsQueryParamConfig = typeof queryParamConfigs;
export type CouponsQueryParams = {
  [key in keyof CouponsQueryParamConfig]: CouponsQueryParamConfig[key] extends QueryParamConfig<
    unknown,
    infer T
  >
    ? T
    : never;
};

type FormFilterParams = Omit<CouponsQueryParams, 'from' | 'to'> & {
  date_range: {
    available_from: moment.Moment | null;
    available_to: moment.Moment | null;
  };
};

const FormWrapper = styled.div`
  padding: 5px 0px;
  margin-bottom: 10px;
  ${MoneySelector} {
    width: 300px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
  & > * {
    ${TextBox} {
      background: white;
    }
    ${DateTimeRange} {
      margin-bottom: 0;
    }
  }
`;

const StyledButton = styled(Button)`
  margin: 5px;
  width: 100px;
`;

const TextLabel = styled.span``;

const DateTimeWrapper = styled.div`
  ${Labeled} {
    display: flex;
    align-items: center;
  }
`;

const MomentParam: QueryParamConfig<Moment | null | undefined> = {
  encode: (value) => (value ? value.toISOString() : null),
  decode: (value) => (value && typeof value === 'string' ? moment(value) : null),
};

const queryParamConfigs = {
  coupon_name: withDefault(StringParam, ''),
  available_from: withDefault(MomentParam, null),
  available_to: withDefault(MomentParam, null),
  issued_shop_name: withDefault(StringParam, ''),
  page: withDefault(NumberParam, 1),
};

const queryParamDefaultValues = getQueryParamDefaultValues(queryParamConfigs);

const CouponListForm = ({
  fetchCoupons,
  page,
}: {
  fetchCoupons: (
    params: CouponsQueryParams & {
      private_money_id: string;
      page: number;
    }
  ) => void;
  page: number;
}) => {
  const formHtmlId = 'coupon-list-form';

  const [query, setQuery] = useQueryParams({
    ...queryParamConfigs,
  });

  const formDefaultValues = {
    coupon_name: query.coupon_name ?? '',
    date_range: {
      available_from: query.available_from ?? null,
      available_to: query.available_to ?? null,
    },
    issued_shop_name: query.issued_shop_name ?? '',
  };

  const { handleSubmit, control, reset, register } = useForm<FormFilterParams>({
    defaultValues: formDefaultValues,
  });

  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);

  useEffect(() => {
    if (userPrivateMoney) {
      fetchCoupons({
        ...query,
        private_money_id: userPrivateMoney.id,
        page,
      });

      reset(formDefaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPrivateMoney, page, query]);

  const onConfirm = (data) => {
    const { date_range, ...rest } = data;

    setQuery({ page: 1, ...date_range, ...rest });
  };

  const clearFilters = () => {
    const { available_from, available_to, ...rest } = queryParamDefaultValues;

    setQuery({ ...queryParamDefaultValues });
    reset({
      date_range: {
        available_from,
        available_to,
      },
      ...rest,
    });
  };

  return (
    <>
      <Headings.h5>検索条件</Headings.h5>
      <FormWrapper>
        <SelectedPrivateMoneyDisplay />
        <FormGroup id={formHtmlId}>
          <LabeledTextBox
            labelText={<TextLabel>クーポン名</TextLabel>}
            {...register('coupon_name')}
          />
          <Controller
            name="date_range"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DateTimeWrapper>
                <Labeled labelText={<TextLabel>利用期間</TextLabel>}>
                  <DateTimeRange
                    dateRange={{
                      from: value?.available_from,
                      to: value?.available_to,
                    }}
                    setDateRange={({ from, to }) =>
                      onChange({ available_from: from ?? null, available_to: to ?? null })
                    }
                    dateTimeProps={{
                      toProps: { showIcon: true, timeFormat: 'HH:mm' },
                      fromProps: { showIcon: true, timeFormat: 'HH:mm' },
                    }}
                  />
                </Labeled>
              </DateTimeWrapper>
            )}
          />
          <LabeledTextBox
            labelText={<TextLabel>発行店舗名</TextLabel>}
            {...register('issued_shop_name')}
          />
        </FormGroup>
        <FormGroup>
          <StyledButton variant="neutral" onClick={clearFilters}>
            クリア
          </StyledButton>
          <StyledButton onClick={handleSubmit(onConfirm)}>検索</StyledButton>
        </FormGroup>
      </FormWrapper>
    </>
  );
};

export default CouponListForm;
